import { useEffect, useRef, useState, type ReactNode } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Loader2, Lock, LogOut } from 'lucide-react';
import { useSettingsStore } from '@shared/stores/settingsStore';
import { useAuthStore } from '@shared/stores/authStore';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { verifyBiometric } from '@shared/lib/biometric';
import { platform } from '@shared/lib/platform';
import { navigateTo } from '@shared/lib/navigation';

/**
 * Biometric app-lock. When the member has opted into biometric unlock
 * (Settings) and there's a stored session, the app is gated behind a
 * device biometric prompt on cold start and whenever it returns from the
 * background. Until verified, an opaque lock screen covers all content.
 */
export function BiometricGate({ children }: { children: ReactNode }): JSX.Element {
  const biometricEnabled = useSettingsStore((s) => s.biometricEnabled);
  const hasSession = useAuthStore((s) => !!(s.accessToken || s.refreshToken));
  const branding = useBrandingStore((s) => s.branding);

  const shouldGate = platform.isNative() && biometricEnabled && hasSession;
  const [locked, setLocked] = useState(shouldGate);
  const [verifying, setVerifying] = useState(false);
  const promptedRef = useRef(false);
  // Synchronous flag the appStateChange listener can read. The Face ID / Touch
  // ID system dialog itself makes the app go inactive→active, which would
  // otherwise be mistaken for a real return-from-background and re-trigger the
  // prompt forever. While a verify is in flight we ignore those transitions.
  const verifyingRef = useRef(false);

  const unlock = async (): Promise<void> => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setVerifying(true);
    try {
      const ok = await verifyBiometric(`Unlock ${branding?.name ?? 'your account'}`);
      if (ok) setLocked(false);
    } finally {
      setVerifying(false);
      // Release a tick later so the active-state event the dialog fires on
      // dismissal is still treated as part of the prompt, not a fresh resume.
      setTimeout(() => {
        verifyingRef.current = false;
      }, 400);
    }
  };

  // Cold-start: lock + auto-prompt once.
  useEffect(() => {
    if (!shouldGate) {
      setLocked(false);
      return;
    }
    if (!promptedRef.current) {
      promptedRef.current = true;
      setLocked(true);
      void unlock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldGate]);

  // Re-lock when the app genuinely returns to the foreground.
  useEffect(() => {
    if (!platform.isNative()) return;
    let remove: (() => void) | undefined;
    let wasBackground = false;
    void CapApp.addListener('appStateChange', ({ isActive }) => {
      // Ignore transitions caused by the biometric dialog itself.
      if (verifyingRef.current) {
        wasBackground = false;
        return;
      }
      if (!isActive) {
        wasBackground = true;
        return;
      }
      if (wasBackground) {
        wasBackground = false;
        const s = useSettingsStore.getState();
        const a = useAuthStore.getState();
        if (s.biometricEnabled && (a.accessToken || a.refreshToken)) {
          setLocked(true);
          void unlock();
        }
      }
    }).then((handle) => {
      remove = () => void handle.remove();
    });
    return () => remove?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSignOut = async (): Promise<void> => {
    await useAuthStore.getState().signOut();
    setLocked(false);
    navigateTo('/login', { replace: true });
  };

  return (
    <>
      {children}
      {locked ? (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-6 bg-paper px-8 text-center pt-safe-or-6 pb-safe-or-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-700">
            <Lock className="h-7 w-7" strokeWidth={1.5} />
          </span>
          <div>
            <h1 className="font-display text-display-lg text-ink">{branding?.name ?? 'Locked'}</h1>
            <p className="mt-1 text-body text-ink-secondary">
              Unlock to continue to your account.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void unlock()}
            disabled={verifying}
            className="inline-flex items-center justify-center gap-2 rounded-pill bg-primary-600 px-6 py-3 font-display text-body font-semibold text-white transition-transform duration-fast active:scale-[0.99] disabled:opacity-70"
          >
            {verifying ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            ) : (
              <Lock className="h-4 w-4" strokeWidth={2} />
            )}
            {verifying ? 'Verifying…' : 'Unlock'}
          </button>
          <button
            type="button"
            onClick={() => void onSignOut()}
            className="inline-flex items-center gap-1.5 text-body-sm font-medium text-ink-muted transition-colors hover:text-ink"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      ) : null}
    </>
  );
}
